import { SIDEBAR_MENUS } from '@/layouts/sidebarMenuConfig';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { orDash } from '@/shared/lib/format';
import { cn } from '@/shared/lib/utils';
import { Button, ConfirmDialog, Icon, PartnerStationLogo } from '@/shared/ui';
import { selectIsAuthenticated, useAuthStore } from '@/stores/authStore';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * 파트너스테이션 공통 레이아웃 (Figma menu + container).
 * 좌측 메뉴(로고 · 업체 · 거래처 정보 · 처방 등록 현황 / 로그인 이메일 · 업체명 · 로그아웃)
 * + 회색 본문 영역. lg 미만에서는 메뉴가 상단 바 + 왼쪽 슬라이드 메뉴로 바뀐다.
 */
export function PartnerLayout() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const refreshMe = useAuthStore((state) => state.refreshMe);
  const navigate = useNavigate();
  const [isLogoutOpen, setLogoutOpen] = useState(false);

  // 진입 시 한 번 세션 확인 겸 메뉴 하단 정보 갱신 (401이면 http-client가 로그인으로 보낸다)
  useEffect(() => {
    if (isAuthenticated) refreshMe().catch(() => undefined);
  }, [isAuthenticated, refreshMe]);

  if (!isAuthenticated) {
    return <Navigate to={ROUTE_PATHS.LOGIN} />;
  }

  const handleLogout = async () => {
    // 로그아웃 → 세션 종료 → 01 로그인
    await logout().catch(() => undefined);
    navigate({ to: ROUTE_PATHS.LOGIN });
  };

  const openLogout = () => setLogoutOpen(true);

  return (
    <div className="flex min-h-screen flex-col bg-surface-white lg:flex-row">
      {/* 데스크탑 — Figma 216px 고정 메뉴 */}
      <aside className="sticky top-0 hidden h-screen w-[216px] shrink-0 flex-col justify-between overflow-y-auto px-6 py-10 lg:flex">
        <div className="flex flex-col gap-10">
          <PartnerStationLogo />
          <SidebarNav />
        </div>
        <SidebarAccount onLogout={openLogout} />
      </aside>

      {/* 태블릿·모바일 — 상단 바 + 슬라이드 메뉴 */}
      <MobileHeader onLogout={openLogout} />

      <ConfirmDialog
        open={isLogoutOpen}
        onOpenChange={setLogoutOpen}
        title="로그아웃하시겠어요?"
        confirmLabel="로그아웃"
        onConfirm={handleLogout}
      />

      <main className="flex min-w-0 flex-1 flex-col items-center gap-6 bg-surface-grey px-4 py-6 sm:px-6 lg:py-10 lg:pr-10 lg:pl-6">
        <Outlet />
      </main>
    </div>
  );
}

/** 메뉴 항목 — 선택: 검정 배경 · 흰 글자 / 기본: tertiary 글자 */
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  return (
    <nav className="flex flex-col gap-2">
      {SIDEBAR_MENUS.map((menu) => {
        const active = pathname.startsWith(menu.to);
        return (
          <Link
            key={menu.to}
            to={menu.to}
            aria-current={active ? 'page' : undefined}
            onClick={onNavigate}
            className={cn(
              'flex h-12 items-center rounded-16 px-2',
              active
                ? 'bg-surface-black text-button-16 font-semibold text-fg-white'
                : 'text-body-16 font-medium text-fg-tertiary hover:bg-grey-100',
            )}
          >
            <Icon name={active ? menu.activeIcon : menu.icon} size={24} />
            <span className="px-1 whitespace-nowrap">{menu.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** 메뉴 하단 — 로그인 이메일 · 업체명 · 로그아웃 */
function SidebarAccount({ onLogout }: { onLogout: () => void }) {
  const user = useAuthStore((state) => state.user);
  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-col text-body-15">
        <span className="font-semibold break-all text-fg-primary">
          {orDash(user?.loginEmail)}
        </span>
        <span className="font-regular text-fg-tertiary">
          {orDash(user?.companyName)}
        </span>
      </div>
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        onClick={onLogout}
      >
        <Icon name="logout" size={16} />
        <span className="px-1">로그아웃</span>
      </Button>
    </div>
  );
}

/** lg 미만 상단 바 — 로고 + 메뉴 버튼. 메뉴는 데스크탑 메뉴와 같은 내용을 왼쪽에서 연다 */
function MobileHeader({ onLogout }: { onLogout: () => void }) {
  const [isOpen, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={setOpen}>
      <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-line-grey bg-surface-white px-4 sm:px-6 lg:hidden">
        <PartnerStationLogo />
        <DialogPrimitive.Trigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="메뉴 열기">
            {/* 디자인에 메뉴 아이콘이 없어 같은 굵기·색의 lucide 아이콘을 쓴다 */}
            <Menu className="size-6 text-fg-tertiary" strokeWidth={1.5} />
          </Button>
        </DialogPrimitive.Trigger>
      </header>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-dim lg:hidden" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 flex w-[264px] max-w-[85vw] flex-col justify-between overflow-y-auto bg-surface-white px-6 py-8 outline-none lg:hidden"
        >
          <DialogPrimitive.Title className="sr-only">
            메뉴
          </DialogPrimitive.Title>
          <div className="flex flex-col gap-8">
            <div className="flex items-center justify-between">
              <PartnerStationLogo />
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="icon-sm" aria-label="메뉴 닫기">
                  <Icon name="close" size={16} />
                </Button>
              </DialogPrimitive.Close>
            </div>
            <SidebarNav onNavigate={close} />
          </div>
          <SidebarAccount
            onLogout={() => {
              close();
              onLogout();
            }}
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
